import { useEffect } from "react";
import "./AboutUs.css";
import { 
  Database,
  Brain,
  Target,
  TrendingUp,
  Zap,
  Users,
  Code,
  BarChart3,
  Cpu,
  Rocket,
  Coffee,
  ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";


// Hero Section
const AboutHero = () => {
  return (
    <section className="about-hero" data-testid="about-hero">
      <div className="about-hero-content">
        <span className="about-label">ABOUT US</span>
        <h1>We Live at the Intersection of Data Science and Performance.</h1>
        <p className="about-hero-subtitle">
          We're not a marketing agency that learned data. We're data scientists 
          who mastered marketing. That difference changes everything.
        </p>
      </div>
    </section>
  );
};

// Mission Section
const AboutMission = () => {
  return (
    <section className="about-mission" data-testid="about-mission">
      <div className="about-mission-content">
        <div className="mission-text">
          <span className="about-section-label">OUR MISSION</span>
          <h2>Turn Data Into Decisions. Decisions Into Growth.</h2>
          <p>
            Every campaign we run, every model we build, every optimization we make 
            is driven by one obsession: measurable outcomes. We don't believe in 
            vanity metrics, gut feelings, or "brand awareness" without attribution.
          </p>
          <p>
            We built IntentLogic because we saw an industry drowning in data but 
            starving for intelligence. Too many companies collect data without 
            knowing what to do with it. Too many agencies promise results without 
            the methodology to deliver them.
          </p>
          <p className="mission-highlight">
            We're here to change that.
          </p>
        </div>
        <div className="mission-visual">
          <div className="mission-stats">
            <div className="mission-stat">
              <Database size={24} />
              <span className="stat-value">245M+</span>
              <span className="stat-label">Consumer Profiles</span>
            </div>
            <div className="mission-stat">
              <Brain size={24} />
              <span className="stat-value">1B+</span>
              <span className="stat-label">Monthly Interactions</span>
            </div>
            <div className="mission-stat">
              <Target size={24} />
              <span className="stat-value">99.2%</span>
              <span className="stat-label">Match Rate</span>
            </div>
            <div className="mission-stat">
              <TrendingUp size={24} />
              <span className="stat-value">3.5x</span>
              <span className="stat-label">Avg. Client ROI</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// What We Believe Section
const AboutBeliefs = () => {
  const beliefs = [
    {
      icon: Code,
      title: "Data Is the Product",
      description: "We don't just use data—we obsess over it. Our proprietary consumer graph is our competitive advantage, refined daily through billions of interactions."
    },
    {
      icon: BarChart3,
      title: "If You Can't Measure It, Don't Do It",
      description: "Every dollar spent should be traceable to an outcome. We built our entire platform around attribution, incrementality, and transparent reporting."
    },
    {
      icon: Cpu,
      title: "AI Should Amplify, Not Replace",
      description: "IRIS, our AI engine, doesn't replace human judgment—it supercharges it. We combine machine learning with domain expertise for decisions neither could make alone."
    },
    {
      icon: Rocket,
      title: "Speed Wins",
      description: "In performance marketing, the fastest learner wins. We iterate in hours, not weeks. Our infrastructure was built for real-time optimization from day one."
    },
    {
      icon: Users,
      title: "Clients Are Partners",
      description: "We don't hide behind dashboards. We share our methodology, explain our models, and work alongside your team to drive results together."
    },
    {
      icon: Coffee,
      title: "Stay Hungry",
      description: "Complacency kills companies. We're constantly testing new channels, building new models, and questioning our own assumptions. The day we stop learning is the day we lose."
    }
  ];

  return (
    <section className="about-beliefs" data-testid="about-beliefs">
      <div className="about-beliefs-content">
        <div className="about-section-header">
          <span className="about-section-label">WHAT WE BELIEVE</span>
          <h2>Principles That Drive Everything We Build</h2>
        </div>
        
        <div className="beliefs-grid">
          {beliefs.map((belief, i) => (
            <div key={i} className="belief-card">
              <div className="belief-icon">
                <belief.icon size={24} />
              </div>
              <h3>{belief.title}</h3>
              <p>{belief.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// The IRIS Story Section
const AboutIRIS = () => {
  return (
    <section className="about-iris" data-testid="about-iris">
      <div className="about-iris-content">
        <div className="iris-visual">
          <div className="iris-diagram">
            <div className="iris-core">
              <Brain size={32} />
              <span>IRIS</span>
            </div>
            <div className="iris-ring ring-1">
              <div className="ring-item">Behavioral Signals</div>
              <div className="ring-item">Intent Data</div>
              <div className="ring-item">Transaction History</div>
            </div>
            <div className="iris-ring ring-2">
              <div className="ring-item">Content Matching</div>
              <div className="ring-item">Offer Optimization</div>
              <div className="ring-item">Channel Selection</div>
            </div>
          </div>
        </div>
        <div className="iris-text">
          <span className="about-section-label">THE BRAIN BEHIND IT ALL</span>
          <h2>Meet IRIS</h2>
          <p>
            IRIS—Intelligent Real-time Intent System—is what happens when you give 
            data scientists carte blanche to build the marketing platform they wished existed.
          </p>
          <p>
            We spent years building IRIS before we ever took a client. Not because 
            we're perfectionists (okay, maybe a little), but because we knew that 
            sustainable competitive advantage comes from infrastructure, not tricks.
          </p>
          <p>
            IRIS processes over a billion consumer interactions monthly, learning 
            what content resonates, what offers convert, and what timing matters—
            for each individual consumer, not segments. That's the difference 
            between spray-and-pray and precision marketing.
          </p>
          <div className="iris-capabilities">
            <div className="iris-cap">
              <Zap size={16} />
              <span>Real-time decisioning</span>
            </div>
            <div className="iris-cap">
              <Target size={16} />
              <span>Individual-level targeting</span>
            </div>
            <div className="iris-cap">
              <TrendingUp size={16} />
              <span>Continuous optimization</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Team Culture Section
const AboutCulture = () => {
  return (
    <section className="about-culture" data-testid="about-culture">
      <div className="about-culture-content">
        <div className="culture-text">
          <span className="about-section-label">OUR CULTURE</span>
          <h2>Built by Nerds. Run Like a Startup.</h2>
          <p>
            We're a team of data scientists, engineers, and marketing strategists 
            who genuinely get excited about incrementality testing and attribution 
            models. (Yes, we know that's weird. We embrace it.)
          </p>
          <p>
            Our office whiteboards are covered in SQL queries and regression formulas. 
            Our Slack channels debate model architectures at midnight. We celebrate 
            when we find a bug in our own assumptions—because that means we're about 
            to get better.
          </p>
          <div className="culture-values">
            <div className="culture-value">
              <span className="value-number">01</span>
              <div className="value-content">
                <h4>Intellectual Honesty</h4>
                <p>We'd rather kill a campaign that isn't working than hide behind excuses.</p>
              </div>
            </div>
            <div className="culture-value">
              <span className="value-number">02</span>
              <div className="value-content">
                <h4>Radical Transparency</h4>
                <p>Clients see what we see. No black boxes, no hidden fees, no BS.</p>
              </div>
            </div>
            <div className="culture-value">
              <span className="value-number">03</span>
              <div className="value-content">
                <h4>Relentless Improvement</h4>
                <p>Yesterday's best practice is today's starting point.</p>
              </div>
            </div>
          </div>
        </div>
        <div className="culture-visual">
          <div className="culture-box">
            <div className="culture-stat-large">
              <span className="stat-number">24/7</span>
              <span className="stat-text">Optimization Never Stops</span>
            </div>
            <div className="culture-badges">
              <div className="badge">Data-Obsessed</div>
              <div className="badge">Results-Driven</div>
              <div className="badge">Always Learning</div>
              <div className="badge">Startup Mentality</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Why We're Different Section
const AboutDifferent = () => {
  const comparisons = [
    {
      traditional: "Rely on third-party data",
      intentlogic: "Own 245M+ first-party consumer profiles"
    },
    {
      traditional: "Campaign-level optimization",
      intentlogic: "Individual-level, real-time decisioning"
    },
    {
      traditional: "Monthly reports after the fact",
      intentlogic: "Live dashboards with full attribution"
    },
    {
      traditional: "Siloed channel execution",
      intentlogic: "Unified cross-channel orchestration"
    },
    {
      traditional: "Creative-led strategy",
      intentlogic: "Data-led strategy with creative testing"
    }
  ];

  return (
    <section className="about-different" data-testid="about-different">
      <div className="about-different-content">
        <div className="about-section-header">
          <span className="about-section-label">WHY WE'RE DIFFERENT</span>
          <h2>We Built What Agencies Can't Buy</h2>
          <p>
            Most agencies outsource data, rent technology, and mark up media. 
            We own our entire stack—from consumer data to activation infrastructure.
          </p>
        </div>
        
        <div className="comparison-table">
          <div className="comparison-header">
            <span>Traditional Agencies</span>
            <span>IntentLogic</span>
          </div>
          {comparisons.map((comp, i) => (
            <div key={i} className="comparison-row">
              <div className="comp-traditional">{comp.traditional}</div>
              <div className="comp-intentlogic">{comp.intentlogic}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const AboutCTA = () => (
  <section className="about-cta-section" data-testid="about-cta">
    <div className="about-cta-content">
      <h2>Ready to Work With People Who Actually Get It?</h2>
      <p>
        If you're tired of agencies who can't explain their methodology, 
        partners who over-promise and under-deliver, and reports that 
        don't tie to revenue—let's talk.
      </p>
      <div className="cta-buttons">
        <Button 
          className="btn-primary-zeta"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          Let's Connect
          <ArrowRight size={18} />
        </Button>
        <Button 
          className="btn-secondary-zeta"
          onClick={() => window.location.href = '/customer-acquisition'}
        >
          See Our Solutions
        </Button>
      </div>
    </div>
  </section>
);

// Main About Us Page Component
function AboutUsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      <div className="about-page" data-testid="about-us-page">
        <SEO 
          title="About Us - Our Mission & Team"
          description="IntentLogic is a performance marketing company powered by proprietary data and AI. Learn about our mission to deliver measurable outcomes through intelligent marketing."
          keywords="about IntentLogic, marketing company, data science team, AI marketing, performance marketing agency, marketing technology, company mission"
          canonical="/about"
        />
        <AboutHero />
        <AboutMission />
        <AboutBeliefs />
        <AboutIRIS />
        <AboutCulture />
        <AboutDifferent />
        <AboutCTA />
      </div>
    </Layout>
  );
}

export default AboutUsPage;
